const API_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';

function getHeaders() {
  const headers = { 'Content-Type': 'application/json' };
  const token = localStorage.getItem('tl_token');
  if (token) headers['Authorization'] = `Bearer ${token}`;
  return headers;
}

function getFetchOptions(extra = {}) {
  return { headers: getHeaders(), credentials: 'include', ...extra };
}

// F5: Request cache + in-flight deduplication
const _cache = new Map();
const _inflight = new Map();
const DEFAULT_TTL = 30000; // 30s default cache TTL

function cachedGet(url, ttl = DEFAULT_TTL) {
  // Return cached data if still fresh (in-memory)
  const cached = _cache.get(url);
  if (cached && Date.now() - cached.ts < ttl) return Promise.resolve(cached.data);

  // Deduplicate in-flight requests
  if (_inflight.has(url)) return _inflight.get(url);

  const promise = fetch(url, getFetchOptions())
    .then(async (resp) => {
      _inflight.delete(url);
      if (!resp.ok) throw new Error(await resp.text());
      const data = await resp.json();
      _cache.set(url, { data, ts: Date.now() });
      return data;
    })
    .catch((err) => {
      _inflight.delete(url);
      throw err;
    });

  _inflight.set(url, promise);
  return promise;
}

// Persistent cache using sessionStorage — survives page refreshes
// Used for slow endpoints (vigilance) that take 20-30s to compute
const PERSIST_TTL = 900000; // 15 minutes

function persistentCachedGet(url, ttl = PERSIST_TTL) {
  // 1. Check in-memory cache first (fastest)
  const memCached = _cache.get(url);
  if (memCached && Date.now() - memCached.ts < ttl) return Promise.resolve(memCached.data);

  // 2. Check sessionStorage (survives page refresh)
  try {
    const stored = sessionStorage.getItem(`tl_${url}`);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Date.now() - parsed.ts < ttl) {
        // Restore into in-memory cache too
        _cache.set(url, { data: parsed.data, ts: parsed.ts });
        return Promise.resolve(parsed.data);
      }
    }
  } catch { /* ignore storage errors */ }

  // 3. Deduplicate in-flight requests
  if (_inflight.has(url)) return _inflight.get(url);

  const promise = fetch(url, getFetchOptions())
    .then(async (resp) => {
      _inflight.delete(url);
      if (!resp.ok) throw new Error(await resp.text());
      const data = await resp.json();
      const ts = Date.now();
      _cache.set(url, { data, ts });
      // Persist to sessionStorage
      try { sessionStorage.setItem(`tl_${url}`, JSON.stringify({ data, ts })); } catch { /* quota */ }
      return data;
    })
    .catch((err) => {
      _inflight.delete(url);
      throw err;
    });

  _inflight.set(url, promise);
  return promise;
}

function buildTldQuery(filters = {}) {
  const params = new URLSearchParams();
  const ids = filters.tldScopeIds || filters.tld_scope_ids || [];
  const roots = filters.rootDomains || filters.root_domains || [];
  ids.forEach((id) => id && params.append('tld_scope_id', id));
  roots.forEach((root) => root && params.append('root_domain', root));
  const qs = params.toString();
  return qs ? `?${qs}` : '';
}

class ThreatLensAPI {
  invalidateCache(pattern) {
    for (const key of _cache.keys()) {
      if (key.includes(pattern)) _cache.delete(key);
    }
  }

  // ===== Company Endpoints =====
  async createCompany(name, domain) {
    const resp = await fetch(`${API_URL}/api/companies`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ name, domain }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async listCompanies() {
    const resp = await fetch(`${API_URL}/api/companies`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getCompany(companyId) {
    const resp = await fetch(`${API_URL}/api/companies/${companyId}`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async deleteCompany(companyId) {
    const resp = await fetch(`${API_URL}/api/companies/${companyId}`, getFetchOptions({ method: 'DELETE' }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== Scan Endpoints =====
  async startScan(companyId, scanType = 'full') {
    this.invalidateCache(companyId);
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/start`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ scan_type: scanType }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getScanStatus(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/status`, getFetchOptions());
    if (!resp.ok) {
      if (resp.status === 404) return null;
      throw new Error(await resp.text());
    }
    return resp.json();
  }

  async getScanResults(companyId) {
    return cachedGet(`${API_URL}/api/scans/${companyId}/results`, 60000);
  }

  async getDashboard(companyId, filters = {}) {
    return cachedGet(`${API_URL}/api/scans/${companyId}/dashboard${buildTldQuery(filters)}`, 60000);
  }

  async getAdversaryLens(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/adversary-lens`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getAttackSurfaceData(companyId, filters = {}) {
    return cachedGet(`${API_URL}/api/scans/${companyId}/attack-surface${buildTldQuery(filters)}`);
  }

  async updateAssetClassification(companyId, hostname, classification) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/assets/classification`, getFetchOptions({
      method: 'PATCH',
      body: JSON.stringify({ hostname, classification }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    this.invalidateCache('attack-surface');
    return resp.json();
  }

  async getAssetProfile(companyId, { hostname, ip, tldScopeIds = [] } = {}) {
    const params = new URLSearchParams();
    if (hostname) params.append('hostname', hostname);
    if (ip) params.append('ip', ip);
    tldScopeIds.forEach((id) => id && params.append('tld_scope_id', id));
    const qs = params.toString() ? `?${params}` : '';
    return cachedGet(`${API_URL}/api/scans/${companyId}/assets/profile${qs}`, 30000);
  }

  // ===== TLD Scope Endpoints =====
  async getTldScopes(companyId) {
    return cachedGet(`${API_URL}/api/tld-scope/${companyId}`, 30000);
  }

  async discoverTldScopes(companyId) {
    this.invalidateCache(`tld-scope/${companyId}`);
    const resp = await fetch(`${API_URL}/api/tld-scope/${companyId}/discover`, getFetchOptions({ method: 'POST' }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async addTldScope(companyId, rootDomain, { activate = false, notes = '' } = {}) {
    this.invalidateCache(`tld-scope/${companyId}`);
    const resp = await fetch(`${API_URL}/api/tld-scope/${companyId}/add`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ root_domain: rootDomain, activate, notes }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async activateTldScope(companyId, scope) {
    this.invalidateCache(`tld-scope/${companyId}`);
    const resp = await fetch(`${API_URL}/api/tld-scope/${companyId}/activate`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(typeof scope === 'string' ? { tld_scope_id: scope } : scope),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async disableTldScope(companyId, scope, notes = '') {
    this.invalidateCache(`tld-scope/${companyId}`);
    const resp = await fetch(`${API_URL}/api/tld-scope/${companyId}/disable`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(typeof scope === 'string' ? { tld_scope_id: scope, notes } : { ...scope, notes }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async rejectTldScope(companyId, scope, notes = '') {
    this.invalidateCache(`tld-scope/${companyId}`);
    const resp = await fetch(`${API_URL}/api/tld-scope/${companyId}/reject`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(typeof scope === 'string' ? { tld_scope_id: scope, notes } : { ...scope, notes }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getTldBilling(companyId) {
    return cachedGet(`${API_URL}/api/tld-scope/${companyId}/billing`, 30000);
  }

  async getVulnerabilities(companyId, filters = {}) {
    return cachedGet(`${API_URL}/api/scans/${companyId}/vulnerabilities${buildTldQuery(filters)}`);
  }

  async getDataExposure(companyId, filters = {}) {
    return cachedGet(`${API_URL}/api/scans/${companyId}/data-exposure${buildTldQuery(filters)}`);
  }

  async getAiFindings(companyId, { executor, category, severity, limit } = {}) {
    const params = new URLSearchParams();
    if (executor) params.append('executor', executor);
    if (category) params.append('category', category);
    if (severity) params.append('severity', severity);
    if (limit) params.append('limit', limit);
    const qs = params.toString() ? `?${params}` : '';
    return cachedGet(`${API_URL}/api/scans/${companyId}/ai-findings${qs}`, 120000);
  }

  // ===== AI Interactive Query =====
  async aiQuery(companyId, question) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/ai-query`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ question }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== Vigilance Endpoints =====
  // These use persistentCachedGet (sessionStorage-backed, 15min TTL)
  // so data survives page refreshes without hitting the slow backend again
  async getThreatLandscape(companyId) {
    return persistentCachedGet(`${API_URL}/api/vigilance/${companyId}/threat-landscape`);
  }

  async getCredentialIntel(companyId) {
    return persistentCachedGet(`${API_URL}/api/vigilance/${companyId}/breach-intelligence`);
  }

  async getBreachIntelligence(companyId) {
    return this.getCredentialIntel(companyId);
  }

  async getCampaigns(companyId) {
    return persistentCachedGet(`${API_URL}/api/vigilance/${companyId}/campaigns`);
  }

  async getBrandProtection(companyId) {
    return persistentCachedGet(`${API_URL}/api/vigilance/${companyId}/brand-protection`);
  }

  async getPredictiveRisk(companyId) {
    return persistentCachedGet(`${API_URL}/api/vigilance/${companyId}/predictive-risk`);
  }

  async getIntelFeed(companyId) {
    return persistentCachedGet(`${API_URL}/api/vigilance/${companyId}/intel-feed`);
  }

  async getTiDigest(companyId) {
    const resp = await fetch(`${API_URL}/api/vigilance/${companyId}/ti-digest`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getTiSources(companyId) {
    const resp = await fetch(`${API_URL}/api/vigilance/${companyId}/ti-sources`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getAttackGraph(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/attack-graph`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async analyzeDetail(companyId, itemType, itemData) {
    const resp = await fetch(`${API_URL}/api/vigilance/${companyId}/analyze-detail`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ item_type: itemType, item_data: itemData }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async updateFindingStatus(companyId, findingId, status, justification = '') {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/findings/${findingId}/status`, getFetchOptions({
      method: 'PUT',
      body: JSON.stringify({ status, justification }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getFindingStates(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/findings/states`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getUnifiedFindings(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.severity) params.append('severity', filters.severity);
    if (filters.status) params.append('status', filters.status);
    if (filters.source) params.append('source', filters.source);
    if (filters.limit) params.append('limit', filters.limit);
    (filters.tldScopeIds || []).forEach((id) => id && params.append('tld_scope_id', id));
    const qs = params.toString() ? `?${params}` : '';
    return cachedGet(`${API_URL}/api/scans/${companyId}/findings${qs}`, 30000);
  }

  async getFindingsPrioritization(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.limit) params.append('limit', filters.limit);
    (filters.tldScopeIds || []).forEach((id) => id && params.append('tld_scope_id', id));
    const qs = params.toString() ? `?${params}` : '';
    return cachedGet(`${API_URL}/api/scans/${companyId}/findings/prioritization${qs}`, 30000);
  }

  async getArtifacts(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.relatedFindingId) params.append('related_finding_id', filters.relatedFindingId);
    if (filters.asset) params.append('asset', filters.asset);
    if (filters.artifactType) params.append('artifact_type', filters.artifactType);
    if (filters.limit) params.append('limit', filters.limit);
    (filters.tldScopeIds || []).forEach((id) => id && params.append('tld_scope_id', id));
    const qs = params.toString() ? `?${params}` : '';
    return cachedGet(`${API_URL}/api/scans/${companyId}/artifacts${qs}`, 30000);
  }

  async getArtifact(companyId, artifactId) {
    return cachedGet(`${API_URL}/api/scans/${companyId}/artifacts/${artifactId}`, 30000);
  }

  async createArtifact(companyId, artifact) {
    this.invalidateCache(`/api/scans/${companyId}/artifacts`);
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/artifacts`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(artifact),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getValidationJobs(companyId, filters = {}) {
    const params = new URLSearchParams();
    if (filters.findingId) params.append('finding_id', filters.findingId);
    if (filters.status) params.append('status', filters.status);
    if (filters.limit) params.append('limit', filters.limit);
    (filters.tldScopeIds || []).forEach((id) => id && params.append('tld_scope_id', id));
    const qs = params.toString() ? `?${params}` : '';
    return cachedGet(`${API_URL}/api/scans/${companyId}/validation-jobs${qs}`, 30000);
  }

  async createValidationJob(companyId, job) {
    this.invalidateCache(`/api/scans/${companyId}/validation-jobs`);
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/validation-jobs`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(job),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async runValidationJob(companyId, validationJobId) {
    this.invalidateCache(`/api/scans/${companyId}/validation-jobs`);
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/validation-jobs/${validationJobId}/run`, getFetchOptions({ method: 'POST' }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getRiskCompliance(companyId, filters = {}) {
    const params = new URLSearchParams();
    (filters.tldScopeIds || []).forEach((id) => id && params.append('tld_scope_id', id));
    const qs = params.toString() ? `?${params}` : '';
    return cachedGet(`${API_URL}/api/scans/${companyId}/risk-compliance${qs}`, 60000);
  }

  async getWorkflowWorkspace(companyId, filters = {}) {
    const params = new URLSearchParams();
    (filters.tldScopeIds || []).forEach((id) => id && params.append('tld_scope_id', id));
    const qs = params.toString() ? `?${params}` : '';
    return cachedGet(`${API_URL}/api/scans/${companyId}/workflow${qs}`, 30000);
  }

  async createRemediationTicket(companyId, ticket) {
    this.invalidateCache(`/api/scans/${companyId}/workflow`);
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/workflow/tickets`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(ticket),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async createSlaPolicy(companyId, policy) {
    this.invalidateCache(`/api/scans/${companyId}/workflow`);
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/workflow/sla-policies`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(policy),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async createWebhookSubscription(companyId, webhook) {
    this.invalidateCache(`/api/scans/${companyId}/workflow`);
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/workflow/webhooks`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(webhook),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getScanHistory(companyId, limit = 30) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/scan-history?limit=${limit}`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getHealth() {
    const resp = await fetch(`${API_URL}/api/health`);
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getFeedHealth() {
    const resp = await fetch(`${API_URL}/api/health/feeds`);
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getProviderStatus() {
    const resp = await fetch(`${API_URL}/api/integrations/provider-status`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getASNInfrastructure(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/asn-infrastructure`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getMonitoringAlerts(companyId, limit = 10) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/monitoring/alerts?limit=${limit}`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async runMonitoringCheck(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/monitoring/run`, getFetchOptions({ method: 'POST' }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== New Enhanced Endpoints =====
  async getWebCrawlResults(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/web-crawl`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getMisconfigurations(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/misconfigurations`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getOwaspResults(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/owasp`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getSecretFindings(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/secrets`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getPortScanResults(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/port-scans`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getSubsidiaryData(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/subsidiaries`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== MFA Endpoints =====
  async getMfaStatus() {
    const resp = await fetch(`${API_URL}/api/auth/mfa/status`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async setupMfa() {
    const resp = await fetch(`${API_URL}/api/auth/mfa/setup`, getFetchOptions({ method: 'POST' }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async confirmMfa(code) {
    const resp = await fetch(`${API_URL}/api/auth/mfa/confirm`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ code }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async verifyMfa(code) {
    const resp = await fetch(`${API_URL}/api/auth/mfa/verify`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ code }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async disableMfa(code) {
    const resp = await fetch(`${API_URL}/api/auth/mfa/disable`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ code }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async enforceMfa(required) {
    const resp = await fetch(`${API_URL}/api/auth/mfa/enforce`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ required }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== Audit Log Endpoints =====
  async getAuditLogs(page = 1, pageSize = 50) {
    const resp = await fetch(`${API_URL}/api/auth/audit-logs?page=${page}&page_size=${pageSize}`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== AI Streaming + Feedback =====
  aiQueryStream(companyId, question, onChunk, onDone) {
    const url = `${API_URL}/api/scans/${companyId}/ai-query/stream`;
    fetch(url, getFetchOptions({
      method: 'POST',
      body: JSON.stringify({ question }),
    })).then(async (resp) => {
      if (!resp.ok) { onDone(await resp.text()); return; }
      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop();
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            try {
              const data = JSON.parse(line.slice(6));
              if (data.text) onChunk(data.text);
              if (data.done) onDone(null);
            } catch { /* ignore parse errors */ }
          }
        }
      }
      onDone(null);
    }).catch((err) => onDone(err.message));
  }

  async submitAiFeedback(companyId, feedback) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/ai-feedback`, getFetchOptions({
      method: 'POST',
      body: JSON.stringify(feedback),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== Executive PDF Report =====
  async downloadExecutivePdf(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/report/executive-pdf`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.blob();
  }

  // ===== Team Members (for assignment dropdowns) =====
  async getTeamMembers() {
    const resp = await fetch(`${API_URL}/api/auth/team`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== Bulk Finding Updates =====
  async bulkUpdateFindings(companyId, findingIds, updates) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/findings/bulk-update`, getFetchOptions({
      method: 'PUT',
      body: JSON.stringify({ finding_ids: findingIds, ...updates }),
    }));
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  // ===== Correlations & Remediation =====
  async getCorrelations(companyId, findingId = null) {
    const params = findingId ? `?finding_id=${findingId}` : '';
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/correlations${params}`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

  async getRemediationPlan(companyId) {
    const resp = await fetch(`${API_URL}/api/scans/${companyId}/remediation-plan`, getFetchOptions());
    if (!resp.ok) throw new Error(await resp.text());
    return resp.json();
  }

}

const api = new ThreatLensAPI();
export default api;
